import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import WaitlistCTA, { WaitlistCTAGroup } from '../components/WaitlistCTA';

export default function FAQ() {
  const navigate = useNavigate();

  const goToWaitlist = (role) => {
    navigate('/?scroll=waitlist&role=' + role);
  };

  return (
    <div className="min-h-screen" data-testid="faq-page">
      <PublicNav currentPage="faq" />

      {/* Section 1: Hero - Light */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-[#334d6e]">Understanding SeedSynch's approach to founder–investor discovery</p>
        </div>
      </section>

      {/* Section 2: About SeedSynch - Dark */}
      <section className="bg-slate-800 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-8">
            <h2 className="text-xl font-bold text-white mb-6">About SeedSynch</h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="what-is" className="border-b border-slate-600">
                <AccordionTrigger className="text-[#c7d4e4] hover:text-blue-400 text-sm">What is SeedSynch?</AccordionTrigger>
                <AccordionContent className="text-[#a3b8d1] text-sm">
                  SeedSynch is a <strong className="text-white">matchmaking platform</strong>—not a funding platform. We facilitate introductions between founders and investors based on declared preferences and criteria.<br/><br/>
                  Most platforms focus on <strong className="text-white">how</strong> capital is deployed (transactions, deals). SeedSynch operates <strong className="text-white">upstream</strong>—we focus on <strong className="text-white">who</strong> founders and investors connect with <strong className="text-white">before</strong> capital discussions begin.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="not-funding" className="border-b border-slate-600">
                <AccordionTrigger className="text-[#c7d4e4] hover:text-blue-400 text-sm">Is SeedSynch a funding platform or lender?</AccordionTrigger>
                <AccordionContent className="text-[#a3b8d1] text-sm">
                  <strong className="text-white">No.</strong> SeedSynch is not a funding platform, lender, crowdfunding site, or investment arranger.<br/><br/>
                  We are a <strong className="text-white">discovery and matchmaking layer</strong>. We help aligned founders and investors find each other and start conversations. We do not:<br/><br/>
                  • Process, hold, or transfer capital<br/>
                  • Structure investments or issue contracts<br/>
                  • Provide investment advice or recommendations<br/>
                  • Take any role in capital transactions<br/><br/>
                  Any partnerships that result from connections made on SeedSynch are arranged directly between the parties.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="no-advice" className="border-b border-slate-600">
                <AccordionTrigger className="text-[#c7d4e4] hover:text-blue-400 text-sm">Do you structure investments or provide advice?</AccordionTrigger>
                <AccordionContent className="text-[#a3b8d1] text-sm">
                  <strong className="text-white">No.</strong> SeedSynch does not structure investments or offer financial, legal, or business advice of any kind.<br/><br/>
                  We facilitate introductions. Users are entirely responsible for:<br/>
                  • Evaluating potential partners<br/>
                  • Structuring their own arrangements<br/>
                  • Seeking independent legal and financial advice<br/>
                  • Ensuring compliance with applicable laws
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="different" className="border-b border-slate-600">
                <AccordionTrigger className="text-[#c7d4e4] hover:text-blue-400 text-sm">How is SeedSynch different from other platforms?</AccordionTrigger>
                <AccordionContent className="text-[#a3b8d1] text-sm">
                  Most platforms focus on the <strong className="text-white">transaction layer</strong>—they facilitate capital deployment or deal flow.<br/><br/>
                  SeedSynch operates <strong className="text-white">before that layer</strong>. We focus on:<br/><br/>
                  • <strong className="text-white">Discovery:</strong> Helping founders and investors find each other based on declared preferences<br/>
                  • <strong className="text-white">Alignment:</strong> Ensuring conversations start with shared context<br/>
                  • <strong className="text-white">Introductions:</strong> Enabling intentional connections, not transactions<br/><br/>
                  Think of SeedSynch as the <strong className="text-white">"who"</strong> layer that comes before the <strong className="text-white">"how"</strong> layer of capital deployment.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Section 3: Subscription & Access - Light */}
      <section className="bg-slate-100 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <h2 className="text-xl font-bold text-blue-900 mb-6">Subscription & Access</h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="why-pay" className="border-b border-slate-200">
                <AccordionTrigger className="text-blue-900 hover:text-blue-500 text-sm">Why do founders and investors pay a subscription?</AccordionTrigger>
                <AccordionContent className="text-blue-700 text-sm">
                  Subscriptions provide access to a <strong className="text-blue-800">curated, intentional environment</strong>. You pay for:<br/><br/>
                  • <strong className="text-blue-800">Time saved:</strong> See profiles that match your declared preferences<br/>
                  • <strong className="text-blue-800">Reduced noise:</strong> A subscription model keeps the platform focused<br/>
                  • <strong className="text-blue-800">Alignment filtering:</strong> Matching based on values and criteria, not just keywords<br/>
                  • <strong className="text-blue-800">Confidential infrastructure:</strong> NDA workflows, secure messaging<br/><br/>
                  SeedSynch does not earn from capital raised or deals closed. We earn from providing access to an aligned network.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="no-commission" className="border-b border-slate-200">
                <AccordionTrigger className="text-blue-900 hover:text-blue-500 text-sm">Does SeedSynch take commissions?</AccordionTrigger>
                <AccordionContent className="text-blue-700 text-sm">
                  <strong className="text-blue-800">No.</strong> SeedSynch does not take any commission, success fee, or percentage of capital from partnerships made between users.<br/><br/>
                  Your subscription fee is your only cost. We do not profit from investment outcomes.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="free-tier" className="border-b border-slate-200">
                <AccordionTrigger className="text-blue-900 hover:text-blue-500 text-sm">Can I use SeedSynch for free?</AccordionTrigger>
                <AccordionContent className="text-blue-700 text-sm">
                  Yes. All roles have a Free tier that allows profile creation and limited browsing.<br/><br/>
                  Paid plans unlock:<br/>
                  • Direct messaging with matches<br/>
                  • NDA-gated content access (pitch decks)<br/>
                  • Unlimited discovery and advanced filters<br/>
                  • Visibility features
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="cancel" className="border-b border-slate-200">
                <AccordionTrigger className="text-blue-900 hover:text-blue-500 text-sm">Can I cancel my subscription anytime?</AccordionTrigger>
                <AccordionContent className="text-blue-700 text-sm">
                  Yes, you can cancel anytime. Your subscription remains active until the end of your current billing period, then reverts to the Free tier.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Section 4: For Users - Dark */}
      <section className="bg-slate-800 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-8">
            <h2 className="text-xl font-bold text-white mb-6">For Founders & Investors</h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="founder-plans" className="border-b border-slate-600">
                <AccordionTrigger className="text-[#c7d4e4] hover:text-blue-400 text-sm">What are the Founder plan options?</AccordionTrigger>
                <AccordionContent className="text-[#a3b8d1] text-sm">
                  <strong className="text-white">Founder Free:</strong> Create a profile, preview investors, limited match suggestions.<br/><br/>
                  <strong className="text-white">Founder Pro:</strong> Access co-founder search, direct messaging, unlimited investor matches, and pitch deck uploads with NDA protection.<br/><br/>
                  <strong className="text-white">Founder Elite:</strong> Everything in Pro, plus featured visibility and curated investor introductions.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="investor-plans" className="border-b border-slate-600">
                <AccordionTrigger className="text-[#c7d4e4] hover:text-blue-400 text-sm">What are the Investor plan options?</AccordionTrigger>
                <AccordionContent className="text-[#a3b8d1] text-sm">
                  <strong className="text-white">Investor Free:</strong> Browse limited venture previews and save to watchlist.<br/><br/>
                  <strong className="text-white">Investor Pro:</strong> Unlimited venture discovery, direct messaging, NDA pitch deck access, and advanced filters.<br/><br/>
                  <strong className="text-white">Investor Premium:</strong> Everything in Pro, plus curated opportunities and priority access.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="nda" className="border-b border-slate-600">
                <AccordionTrigger className="text-[#c7d4e4] hover:text-blue-400 text-sm">How does the NDA process work?</AccordionTrigger>
                <AccordionContent className="text-[#a3b8d1] text-sm">
                  NDA-gated content (pitch decks, financials) is available to paid subscribers. When you request access, you accept the founder's NDA terms. Once accepted, you have immediate access. All NDA acceptances are logged with timestamps.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Section 5: Security - Light */}
      <section className="bg-slate-100 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <h2 className="text-xl font-bold text-blue-900 mb-6">Security & Data</h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="security" className="border-b border-slate-200">
                <AccordionTrigger className="text-blue-900 hover:text-blue-500 text-sm">Is my data secure?</AccordionTrigger>
                <AccordionContent className="text-blue-700 text-sm">
                  Yes. We use industry-standard encryption and secure access controls. Sensitive information like pitch decks is only accessible after NDA acceptance. All passwords are hashed.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="payments" className="border-b border-slate-200">
                <AccordionTrigger className="text-blue-900 hover:text-blue-500 text-sm">What payment methods do you accept?</AccordionTrigger>
                <AccordionContent className="text-blue-700 text-sm">
                  We accept major credit cards (Visa, Mastercard, Amex) and support payments in multiple currencies.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Section 6: CTA - Dark */}
      <section className="bg-slate-800 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-xl font-bold text-white mb-4">Still have questions?</h2>
          <p className="text-[#c7d4e4] mb-8 text-sm">Contact our support team at hello@seedsynch.com</p>
          <WaitlistCTAGroup 
            onFounderClick={() => goToWaitlist('founder')}
            onInvestorClick={() => goToWaitlist('investor')}
            theme="dark"
            size="default"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
